/*
 * Copyright 2020 Ladifire. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import React, {ReactElement} from 'react';
import {AriaLabelingProps, DOMProps, StyleProps} from '@react-ui-types/shared';
import {useSlotProps, useStyleProps, joinClasses} from '@ladifire-ui-react/utils';
import {filterDOMProps} from '@react-ui-aria/utils';
import {useProvider} from '@ladifire-ui-react/provider';

import stylex from "@ladifire/stylex";

type Style =
  | 'root';

const styles = stylex.create<Style>({
  root: {
    position: 'relative',
  },
});

interface SlotWrapperProps extends DOMProps, AriaLabelingProps, StyleProps {
  children: ReactElement,
  slot?: string,
  /**
   * Indicates whether the element is exposed to an accessibility API.
   */
  'aria-hidden'?: boolean
}

export function SlotWrapper(props: SlotWrapperProps) {
  props = useSlotProps(props, 'icon');
  let {
    children,
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    ...otherProps
  } = props;

  let {styleProps} = useStyleProps(otherProps);
  let provider = useProvider();
  let scale = 'M';
  if (provider !== null) {
    scale = provider.scale === 'large' ? 'L' : 'M';
  }

  if (!ariaHidden) {
    ariaHidden = undefined;
  }

  return React.cloneElement(children, {
    ...filterDOMProps(otherProps),
    ...styleProps,
    scale,
    'aria-label': ariaLabel,
    'aria-hidden': (ariaLabel ? (ariaHidden || undefined) : true),
    className: joinClasses(
      stylex(styles.root),
      children.props.className,
      styleProps.className
    )
  });
}
